// src/lib/useSpeechInput.js
//
// Voice input for AVIA using the browser's built-in Web Speech API. No paid
// provider, no upload from this code and no audio kept: the browser returns
// text only. (Chrome's implementation may process audio on the vendor's
// servers; that is the browser's service, disclosed in the UI.)
//
// The transcript is never sent automatically: it is handed to the caller to
// show in the composer, where the patient can correct it before sending.
//
// States: idle | listening | error. Recognition stops on stop(), on unmount
// and when the caller navigates away (unmount).

import { useCallback, useEffect, useRef, useState } from "react";

export const VOICE_ERRORS = {
  unsupported: "Voice input isn't supported in this browser. Please type your message instead.",
  "not-allowed": "Microphone access was blocked. Allow the microphone in your browser settings, or type your message instead.",
  "service-not-allowed": "Voice input isn't available here. Please type your message instead.",
  "audio-capture": "No microphone was found. Please type your message instead.",
  "no-speech": "I didn't hear anything. Try again, or type your message.",
  network: "Voice input needs a network connection. Please type your message instead.",
  aborted: "",
  generic: "Voice input stopped unexpectedly. Please try again or type your message.",
};

export function speechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function useSpeechInput({ onTranscript, lang } = {}) {
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [interim, setInterim] = useState("");
  const recognitionRef = useRef(null);
  const finalRef = useRef("");
  const callbackRef = useRef(onTranscript);
  callbackRef.current = onTranscript;
  const supported = Boolean(speechRecognitionCtor());

  const cleanup = useCallback(() => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        // already stopped
      }
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(() => {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) {
      setState("error");
      setError(VOICE_ERRORS.unsupported);
      return;
    }
    cleanup();
    finalRef.current = "";
    setInterim("");
    setError("");
    let recognition;
    try {
      recognition = new Ctor();
    } catch {
      setState("error");
      setError(VOICE_ERRORS.unsupported);
      return;
    }
    recognition.lang = lang || (typeof navigator !== "undefined" && navigator.language) || "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      let finalText = finalRef.current;
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript || "";
        if (result.isFinal) finalText += text;
        else interimText += text;
      }
      finalRef.current = finalText;
      setInterim(interimText);
    };
    recognition.onerror = (event) => {
      const message = VOICE_ERRORS[event?.error] ?? VOICE_ERRORS.generic;
      if (message) {
        setError(message);
        setState("error");
      }
    };
    recognition.onend = () => {
      const text = finalRef.current.trim();
      recognitionRef.current = null;
      setInterim("");
      setState((current) => (current === "error" ? current : "idle"));
      if (text) callbackRef.current?.(text);
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setState("listening");
    } catch {
      recognitionRef.current = null;
      setState("error");
      setError(VOICE_ERRORS.generic);
    }
  }, [cleanup, lang]);

  // Stop listening; whatever was heard is still delivered for review.
  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      cleanup();
      setState("idle");
    }
  }, [cleanup]);

  // Discard everything heard so far.
  const cancel = useCallback(() => {
    finalRef.current = "";
    cleanup();
    setInterim("");
    setState("idle");
  }, [cleanup]);

  const dismissError = useCallback(() => {
    setError("");
    setState("idle");
  }, []);

  return { supported, state, error, interim, start, stop, cancel, dismissError };
}
